import { getDesignerComponentCatalog } from "../../designer-component-catalog-actions";
import { getValues } from "../../designer-context";
import {
  checkDesignerScreenCompleteness,
  findDesignerComponentsByName,
  getDesignerChildren,
  searchDesignerComponents,
  getDesignerComponentEvents,
  getDesignerComponentHierarchy,
  getDesignerComponentInfo,
  getDesignerComponentStyles,
  getDesignerMultipleComponentInfo,
  getDesignerMultipleComponentStyles,
  getDesignerPropertyValues,
  getDesignerScreenTree,
  getDesignerSelectedComponent,
  listAllDesignerComponents,
  listDesignerAvailableComponents
} from "../../designer-screen-query-actions";
import {
  composeManagedScreenBindingSources,
  managedBindingContextFromSdState,
  resolveManagedScreenBindingSources,
  type IManagedBindingSource,
  type IManagedBindingSourceContext,
  type IManagedBindingSourceResolution,
  type IManagedBindingWorkflowLike
} from "../../managed-screen-binding-sources";
import { fetchManagedScreenWorkflow } from "../../managed-screen-workflow-fetch";
import { findManagedTaskScreenLink } from "../../managed-task-screen";
import {
  buildScreenBindingInventory,
  type IInventoryDisplayItem,
  type IScreenBindingInventory
} from "../../screen-binding-inventory";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

export const screenDesignTools: ToolHandlerGroup = {
  sync: {
    ui_common_get_screen_tree: (input) => getDesignerScreenTree(getSdInstance(), input),
    ui_common_get_component_info: (input) => getDesignerComponentInfo(getSdInstance(), input.kuikaId),
    ui_common_get_selected_component: () => getDesignerSelectedComponent(getSdInstance()),
    ui_common_list_available_components: (input) => listDesignerAvailableComponents(getSdInstance(), input),
    ui_common_get_component_catalog: (input) => getDesignerComponentCatalog(getSdInstance(), input),
    ui_common_get_children: (input) => getDesignerChildren(getSdInstance(), input.kuikaId),
    ui_common_get_component_events: (input) => getDesignerComponentEvents(getSdInstance(), input.kuikaId),
    ui_common_get_component_styles: (input) => getDesignerComponentStyles(getSdInstance(), input.kuikaId),
    ui_common_get_multiple_component_styles: (input) => getDesignerMultipleComponentStyles(getSdInstance(), input),
    ui_common_get_multiple_component_info: (input) => getDesignerMultipleComponentInfo(getSdInstance(), input),
    ui_common_get_component_hierarchy: (input) => getDesignerComponentHierarchy(getSdInstance(), input),
    ui_common_find_components_by_name: (input) => findDesignerComponentsByName(getSdInstance(), input.componentName),
    ui_common_list_all_components: () => listAllDesignerComponents(getSdInstance()),
    ui_common_get_property_value: (input) => getDesignerPropertyValues(getSdInstance(), input),
    ui_common_search_components: (input) => searchDesignerComponents(getSdInstance(), input),
    ui_common_check_completeness: () => checkDesignerScreenCompleteness(getSdInstance())
  },

  async: {
    /**
     * KOA-18136 — the process values THIS screen carries, if it is a
     * platform-managed workflow task screen.
     *
     * A task screen declares no screen inputs and hosts no repeater, so a
     * caller that only knows the ordinary binding sources concludes there is
     * nothing to bind an action parameter to — and attaches the action with
     * empty parameters, which is precisely what shipped a dead "previous
     * requests" button in the 2026-08-11 smoke. The values ARE there (the
     * platform loads the step instance's bag before pageInit runs) and the
     * symbol picker has always offered them; this makes the same offer
     * readable outside the cascader.
     *
     * Empty list on an ordinary screen — that IS the answer, not a failure.
     *
     * `processId` (optional) names the process the CALLER knows this screen was
     * generated for. It exists because the browser's screen→step registry is not
     * a dependable identity for a screen created moments ago: the 2026-08-12
     * live smoke returned an EMPTY inventory for both freshly generated task
     * screens, while the very same registry had answered during the build (it is
     * the only thing either path that learns a task screen reads). With the id
     * supplied, the inventory is built from the process itself and the registry
     * is not consulted at all — the identity comes from whoever created the
     * screen, which is the one party that cannot be wrong about it.
     */
    ui_common_list_binding_sources: async (input) => {
      const sd = getValues(getSdInstance());
      const ctx: IManagedBindingSourceContext = managedBindingContextFromSdState(sd);
      const requestedProcessId: string = typeof input?.processId === "string" ? input.processId.trim() : "";
      let resolution: IManagedBindingSourceResolution = resolveManagedScreenBindingSources(ctx);
      let identity: "screen-link" | "caller-process" = "screen-link";

      // The screen IS managed but no source in designer state carried the
      // workflow's parameters — fetch it. Without this the inventory silently
      // shrinks to the four platform values and every consumer reads that as
      // "the process carries nothing else", which is how a task screen's
      // pageInit reads shipped with empty keys (KOA-18136). A truncated
      // inventory is indistinguishable from a complete one, so it must never
      // be returned while a fetch can still complete it.
      if (resolution.workflowParametersMissing && resolution.processId !== undefined) {
        const fetched: IManagedBindingWorkflowLike | undefined = await fetchManagedScreenWorkflow(resolution.processId);
        if (fetched !== undefined) {
          resolution = resolveManagedScreenBindingSources({ ...ctx, fetchedWorkflow: fetched });
        }
      }

      // The registry does not know this screen, but the caller named the process
      // it belongs to. Build the inventory from that process directly.
      let sources: IManagedBindingSource[] = resolution.sources;
      let workflowParametersMissing: boolean = resolution.workflowParametersMissing;
      if (sources.length === 0 && requestedProcessId.length > 0) {
        const fetched: IManagedBindingWorkflowLike | undefined = await fetchManagedScreenWorkflow(requestedProcessId);
        if (fetched !== undefined) {
          sources = composeManagedScreenBindingSources(fetched);
          workflowParametersMissing = false;
          identity = "caller-process";
        } else {
          workflowParametersMissing = true;
        }
      }

      // The process values are only one of the three families a parameter can be
      // filled from. The screen's own controls and the results of the actions
      // already on it are just as real, and on a task screen there is no other
      // pass that sees them — the deterministic Binder only runs on ordinary
      // screens. Offering process values alone is what left a category filter
      // empty next to the select box that was built to feed it (live
      // 2026-08-12), so the answer carries the whole screen.
      const screenDesign = getSdInstance().screenProvider?.getScreenDesign?.();
      const inventory: IScreenBindingInventory = buildScreenBindingInventory({
        processSources: sources,
        displayItems: (screenDesign?.displayItems ?? []) as IInventoryDisplayItem[],
        componentDefinitions: sd?.project?.componentSetObj?.components ?? [],
        actionDefinitions: sd?.actionDefinitions ?? []
      });

      return {
        // KOA-18136 — the SCREEN's own identity, never "the caller named a
        // process". A task feed on an approver's inbox is enumerated by passing
        // `processId` from an ORDINARY screen, and reporting that screen as
        // platform-managed would tell the caller the wizard owns a body it fully
        // owns itself. Nothing consumed this field yet; it is fixed before
        // something does.
        isManagedTaskScreen: identity === "screen-link" && sources.length > 0,
        count: inventory.sources.length,
        /** Which side proved this screen belongs to a process. */
        identity,
        // Reported, never hidden: a caller that binds off an inventory missing
        // the workflow's own parameters is about to write dead bindings, and it
        // can only refuse if it is told.
        workflowParametersMissing,
        /** True when the offer was capped — the rest exists, it is just not listed. */
        truncated: inventory.truncated,
        // An empty PROCESS inventory used to be indistinguishable from "ordinary
        // screen", so the pass that depends on it died silently twice. When
        // there is nothing to offer, say what was actually looked at.
        ...(sources.length === 0 ? { diagnostics: emptyInventoryDiagnostics(ctx, requestedProcessId) } : {}),
        sources: inventory.sources
      };
    }
  }
};

/** What was consulted when the inventory came back empty. */
interface IEmptyInventoryDiagnostics {
  readonly screenId: string;
  /** How many screen→step links the project currently holds. */
  readonly linkCount: number;
  /** True when one of them names THIS screen (so the miss is elsewhere). */
  readonly screenLinked: boolean;
  /** Whether the caller named a process, and whether fetching it worked. */
  readonly requestedProcessId: string;
}

/**
 * The state behind an empty answer.
 *
 * Not decoration: "this screen is not managed" and "the registry no longer holds
 * the link" produce the identical empty list, and telling them apart cost a live
 * smoke. `linkCount` + `screenLinked` separate them in one line.
 */
function emptyInventoryDiagnostics(
  ctx: IManagedBindingSourceContext,
  requestedProcessId: string
): IEmptyInventoryDiagnostics {
  const links = Array.isArray(ctx.project?.processStepScreenIds) ? ctx.project.processStepScreenIds : [];
  return {
    screenId: ctx.screenId ?? "",
    linkCount: links.length,
    screenLinked: findManagedTaskScreenLink(ctx.project, ctx.screenId) !== null,
    requestedProcessId
  };
}
